/**
 * PDP promo: apply the promo code on click.
 *
 * Any element with `data-wf-promo-copy="<code>"` applies that code when clicked
 * (the promo price line under the price, the promo block, and an announcement
 * bar message with a code). Links inside it keep working and apply nothing.
 *
 * /discount/<code> stores the code for the session, so Shopify applies it at
 * checkout even if the cart is still empty. A cart that already has items gets
 * the code added too, so the cart drawer shows it. If that fails, or Shopify
 * says the code does not apply to the cart, the code is copied to the
 * clipboard instead, as before.
 *
 * The element itself, or a descendant marked `data-wf-promo-copy-label`, shows
 * its `data-applied-text` (or `data-copied-text` after the fallback) for a
 * moment as confirmation; screen readers get it from a hidden status region.
 *
 * One listener on the document, so it keeps working after product-price.js
 * swaps the price markup on a variant change.
 *
 * The code is also applied without a click on promo PDPs and in the cart; see
 * autoApply() below.
 */

import { DiscountUpdateEvent } from '@theme/events';

const RESET_DELAY = 2000;

const root = window.Shopify?.routes?.root ?? '/';

/**
 * @param {string} code
 * @returns {Promise<boolean>}
 */
async function applyCode(code) {
  try {
    const stored = await fetch(`${root}discount/${encodeURIComponent(code)}?redirect=${root}cart.js`, {
      credentials: 'same-origin',
    });
    if (!stored.ok) return false;

    // Add it to a cart with items too, keeping any codes already there, the
    // way the cart's own discount form does (assets/cart-discount.js)
    const cart = await (await fetch(`${root}cart.js`, { credentials: 'same-origin' })).json();
    if (!cart.item_count) return true;

    /** @param {{ code: string }} discount */
    const isCode = (discount) => discount.code.toLowerCase() === code.toLowerCase();
    let result = cart;
    if (!(cart.discount_codes ?? []).some(isCode)) {
      const codes = (cart.discount_codes ?? []).map((/** @type {{ code: string }} */ discount) => discount.code);
      const updated = await fetch(`${root}cart/update.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: [...codes, code].join(',') }),
      });
      if (!updated.ok) return false;
      result = await updated.json();
    }
    document.dispatchEvent(new DiscountUpdateEvent(result, 'wf-pdp-promo'));

    // Shopify accepts any code; with items in the cart it says whether this
    // one takes effect (an ended code, or no eligible items, does not)
    return (result.discount_codes ?? []).some(
      (/** @type {{ code: string; applicable: boolean }} */ discount) => isCode(discount) && discount.applicable
    );
  } catch (e) {
    return false;
  }
}

/**
 * @param {string} text
 * @returns {Promise<boolean>}
 */
async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (e) {
    // Older browsers or a denied permission: fall back to a hidden textarea
    const field = document.createElement('textarea');
    field.value = text;
    field.setAttribute('readonly', '');
    field.style.position = 'fixed';
    field.style.opacity = '0';
    document.body.append(field);
    field.select();
    let copied = false;
    try {
      copied = document.execCommand('copy');
    } catch (err) {
      copied = false;
    }
    field.remove();
    return copied;
  }
}

/**
 * Screen readers hear the confirmation from one hidden status region that
 * lives outside the price markup, which product-price.js replaces.
 *
 * @param {string} message
 */
function announce(message) {
  let region = document.getElementById('wf-pdp-promo-status');
  if (!region) {
    region = document.createElement('span');
    region.id = 'wf-pdp-promo-status';
    region.className = 'visually-hidden';
    region.setAttribute('role', 'status');
    document.body.append(region);
  }
  region.textContent = '';
  window.setTimeout(() => {
    if (region) region.textContent = message;
  }, 50);
}

/** Original markup of labels showing their confirmation, restored afterwards */
const originalMarkup = new WeakMap();
/** @type {WeakMap<HTMLElement, number>} */
const resetTimers = new WeakMap();

/**
 * Swaps the label for its confirmation text, then puts the original markup
 * back. Markup, not text: an announcement carries links (See T&Cs.).
 *
 * @param {HTMLElement} label
 * @param {string | undefined} message
 */
function showConfirmation(label, message) {
  if (!message) return;

  if (!originalMarkup.has(label)) originalMarkup.set(label, label.innerHTML);
  label.textContent = message;
  label.classList.add('is-copied');

  window.clearTimeout(resetTimers.get(label));
  resetTimers.set(
    label,
    window.setTimeout(() => {
      label.innerHTML = originalMarkup.get(label) ?? label.innerHTML;
      label.classList.remove('is-copied');
      originalMarkup.delete(label);
    }, RESET_DELAY)
  );
}

if (!window.wfPdpPromoCopyBound) {
  window.wfPdpPromoCopyBound = true;

  document.addEventListener('click', async (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const source = target?.closest('[data-wf-promo-copy]');
    if (!(source instanceof HTMLElement) || target?.closest('a')) return;

    const code = source.dataset.wfPromoCopy;
    if (!code) return;

    const label = source.matches('[data-wf-promo-copy-label]')
      ? source
      : source.querySelector('[data-wf-promo-copy-label]');

    if (await applyCode(code)) {
      if (label instanceof HTMLElement) showConfirmation(label, label.dataset.appliedText);
      announce(`Code ${code} applied at checkout`);
      return;
    }

    if (!(await copyText(code))) return;
    if (label instanceof HTMLElement) showConfirmation(label, label.dataset.copiedText);
    announce(`Code ${code} copied`);
  });

  autoApply();
}

/**
 * Applies the code without a click, once per browser session for each step,
 * so a shopper who removes it or enters another code is not overridden:
 *
 * - A promo PDP (promo price line or promo block) stores the code for the
 *   session, so checkout gets it even through an express button.
 * - The cart's "Use code" button (snippets/cart-products.liquid) only renders
 *   on a line with the promo tag while the code is not applied; when it shows
 *   up, on the cart page or in the drawer after an add, the code is added to
 *   the cart so it shows there too.
 */
function autoApply() {
  /** @param {string} key */
  const claim = (key) => {
    try {
      if (sessionStorage.getItem(key)) return false;
      sessionStorage.setItem(key, '1');
      return true;
    } catch (e) {
      // No storage: skip, rather than risk applying on every render
      return false;
    }
  };

  const pdpSource = document.querySelector('.wf-pdp-promo-price[data-wf-promo-copy], .wf-pdp-promo[data-wf-promo-copy]');
  const pdpCode = pdpSource instanceof HTMLElement ? pdpSource.dataset.wfPromoCopy : '';
  if (pdpCode && claim(`wf-promo-session:${pdpCode.toLowerCase()}`)) {
    fetch(`${root}discount/${encodeURIComponent(pdpCode)}?redirect=${root}cart.js`, {
      credentials: 'same-origin',
    }).catch(() => {});
  }

  let scheduled = false;
  const checkCart = () => {
    scheduled = false;
    const button = document.querySelector('.cart-items__promo-apply[data-wf-promo-copy]');
    const code = button instanceof HTMLElement ? button.dataset.wfPromoCopy : '';
    if (!code) return;
    if (claim(`wf-promo-cart:${code.toLowerCase()}`)) applyCode(code);
    observer.disconnect();
  };
  // The drawer re-renders its lines after an add, so watch for the button
  const observer = new MutationObserver(() => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(checkCart);
  });
  observer.observe(document.body, { childList: true, subtree: true });
  checkCart();
}
